import { zh } from '../../utils/zh';
import { useState, useEffect, useCallback } from 'react';
import type { DomainConfig, SenderAccount } from '../layout/AccountSwitcher';
import { getRegionDomain, getCommonRegion, getRegionLabel } from '../../utils/regionMap';
import { getCountryLanguage, isEnglishCountry, getLanguagesForCountries } from '../../utils/countryLanguage';
import { authFetch } from '../../utils/authFetch';
import * as XLSX from 'xlsx';

interface Lead {
    id: string;
    campaignId: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    country: string;
    status: string;
    emailsSentCount: number;
    sourceUrl?: string;
    customFields?: Record<string, string>;
}

interface Campaign {
    id: string;
    name: string;
}

interface Thread {
    id: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string;
    toEmail: string;
    subject: string;
    bodyText: string;
    receivedAt: string;
}

const REGION_ICONS: Record<string, string> = {
    ASIA: '🌏', EU: '🇪🇺', LAT: '🌎',
    US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', AR: '🇦🇷', UY: '🇺🇾', BR: '🇧🇷',
    CO: '🇨🇴', CL: '🇨🇱', ES: '🇪🇸', FR: '🇫🇷', DE: '🇩🇪', GB: '🇬🇧',
    AE: '🇦🇪', SG: '🇸🇬', AU: '🇦🇺',
};

const PLACEHOLDERS = ['{{name}}', '{{company}}', '{{title}}', '{{country}}', '{{senderName}}', '{{senderEmail}}'];

function getPracticeAreas(lead: Lead): string {
    const fields = lead.customFields;
    return [fields?.['所属领域'], fields?.['法律领域']]
        .find(value => typeof value === 'string' && value.trim())?.trim() || '';
}

function renderTemplate(template: string, vars: Record<string, string>): string {
    let result = template;
    for (const [key, val] of Object.entries(vars)) {
        result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val);
    }
    return result;
}

interface Props {
    accounts: DomainConfig[];
}

export function ContactsPage({ accounts }: Props) {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
    const [threads, setThreads] = useState<Thread[]>([]);

    // ── Multi-select & Auto-send ──
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showCompose, setShowCompose] = useState(false);
    const [fromEmail, setFromEmail] = useState(accounts[0]?.senders[0]?.email || '');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });

    const allSenders = accounts.flatMap(d => d.senders);

    // ── Region-locked sending ──
    // Determine which region the selected contacts belong to
    const selectedLeadsForRegion = leads.filter(l => selectedIds.has(l.id));
    const selectedCountries = selectedLeadsForRegion.map(l => l.country);
    const commonRegion = getCommonRegion(selectedCountries); // e.g. "lawhelper.asia"
    const isMixedRegion = selectedCountries.length > 0 && !commonRegion;

    // Only show senders from the matching region
    const allowedSenders = commonRegion
        ? allSenders.filter(s => s.domain === commonRegion)
        : allSenders;

    // Auto-select the first allowed sender when region changes
    const currentFromValid = allowedSenders.some(s => s.email === fromEmail);
    const effectiveFromEmail = currentFromValid ? fromEmail : (allowedSenders[0]?.email || '');
    const sender = allSenders.find(s => s.email === effectiveFromEmail);

    const load = useCallback(async () => {
        try {
            const res = await authFetch('/campaigns');
            const data = await res.json();
            const campaignList = data.campaigns || [];
            setCampaigns(campaignList);
            const all: Lead[] = [];
            for (const c of campaignList) {
                try {
                    const lr = await authFetch(`/campaigns/${c.id}`);
                    const ld = await lr.json();
                    
                    // The API returns the leads array wrapper or directly the array depending on the endpoint version 
                    const leadsArray = Array.isArray(ld) ? ld : (ld.leads || []);
                    all.push(...leadsArray);
                } catch { }
            }
            // ── Dedup by email: keep the most recent (last in array) ──
            const emailMap = new Map<string, Lead>();
            for (const lead of all) {
                if (!lead || !lead.email) continue;
                const key = lead.email.toLowerCase();
                emailMap.set(key, lead); // later entries overwrite earlier = keep latest
            }
            const deduped = Array.from(emailMap.values());
            setLeads(deduped);
            if (all.length !== deduped.length) {
                console.log(`[Contacts] Deduped: ${all.length} → ${deduped.length} (${all.length - deduped.length} duplicates removed)`);
            }
        } catch { }
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectLead = async (lead: Lead) => {
        setSelectedLead(lead);
        try {
            const res = await authFetch(`/leads/${lead.id}/threads`);
            const data = await res.json();
            setThreads(data.threads || []);
        } catch { setThreads([]); }
    };

    const countries = Array.from(new Set(leads.map(l => l.country))).sort();
    const statuses = Array.from(new Set(leads.map(l => l.status))).sort();

    const filtered = leads.filter(l => {
        if (statusFilter !== 'all' && l.status !== statusFilter) return false;
        if (countryFilter !== 'all' && l.country !== countryFilter) return false;
        if (search) {
            const s = search.toLowerCase();
            return (l.name || '').toLowerCase().includes(s)
                || (l.companyName || '').toLowerCase().includes(s)
                || (l.email || '').toLowerCase().includes(s)
                || (l.title || '').toLowerCase().includes(s)
                || getPracticeAreas(l).toLowerCase().includes(s);
        }
        return true;
    });

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === filtered.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filtered.map(l => l.id)));
        }
    };

    const downloadExcel = () => {
        const exportData = filtered.map(l => ({
            'Name': l.name,
            'Title': l.title,
            '所属领域': getPracticeAreas(l),
            'Company': l.companyName,
            'Email': l.email,
            'Country': l.country,
            'Status': l.status,
            'Emails Sent': l.emailsSentCount,
            'Campaign': campaigns.find(c => c.id === l.campaignId)?.name || '',
            'Source URL': l.sourceUrl || '',
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        // Auto-size columns
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({
            wch: key === '所属领域' ? 55 : Math.max(key.length, ...exportData.map(r => String((r as any)[key] || '').length).slice(0, 100)) + 2
        }));
        ws['!cols'] = colWidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "联系人");
        const fileName = `contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const selectedLeads = leads.filter(l => selectedIds.has(l.id));

    const getPreviewVars = (lead: Lead) => ({
        name: lead.name,
        company: lead.companyName,
        title: lead.title,
        country: lead.country,
        senderName: sender?.displayName || '',
        senderEmail: sender?.email || '',
    });

    const autoSend = async () => {
        if (!effectiveFromEmail || selectedLeads.length === 0 || !emailSubject || !emailBody || isMixedRegion) return;
        setSending(true);
        setSendProgress({ sent: 0, total: selectedLeads.length });

        for (let i = 0; i < selectedLeads.length; i++) {
            const lead = selectedLeads[i];
            const vars = getPreviewVars(lead);
            try {
                await authFetch('/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        leadId: lead.id,
                        toEmail: lead.email,
                        fromEmail: effectiveFromEmail,
                        subject: renderTemplate(emailSubject, vars),
                        bodyText: renderTemplate(emailBody, vars),
                    }),
                });
            } catch (e) {
                console.error(`Failed to send to ${lead.email}:`, e);
            }
            setSendProgress({ sent: i + 1, total: selectedLeads.length });
        }

        setSending(false);
        setShowCompose(false);
        setSelectedIds(new Set());
        setEmailSubject('');
        setEmailBody('');
        load();
    };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 className="page-title">👤 联系人</h2>
                    <p className="page-subtitle">{leads.length} 位联系人，来自 {campaigns.length} 个活动</p>
                </div>
                {selectedIds.size > 0 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {isMixedRegion && (
                            <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
                                ⚠️ 联系人来自多个地区，请选择同一地区的联系人
                            </span>
                        )}
                        {commonRegion && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {getRegionLabel(commonRegion)}
                            </span>
                        )}
                        <button className="btn btn-primary" onClick={() => setShowCompose(true)}
                            disabled={isMixedRegion}>
                            ✉️ 发送邮件至 {selectedIds.size} 联系人{selectedIds.size > 1 ? 's' : ''}
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="input" style={{ maxWidth: 340 }} placeholder="🔍 搜索姓名、公司、邮箱或所属领域…"
                    value={search} onChange={(e) => setSearch(e.target.value)} />

                <select className="input" style={{ width: 'auto' }} value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">全部状态</option>
                    {statuses.map(s => <option key={s} value={s}>{zh(s)}</option>)}
                </select>

                <select className="input" style={{ width: 'auto' }} value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}>
                    <option value="all">全部国家</option>
                    {countries.map(c => <option key={c} value={c}>{REGION_ICONS[c] || '🌍'} {c}</option>)}
                </select>

                <span style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                    {filtered.length} 条结果
                    {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                </span>

                <button className="btn btn-outline" onClick={downloadExcel}
                    disabled={filtered.length === 0}
                    style={{ marginLeft: 'auto', fontSize: 12 }}>
                    📥 导出 Excel（{filtered.length})
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: selectedLead ? 'minmax(0, 1fr) 400px' : 'minmax(0, 1fr)', gap: 16 }}>
                {/* Table */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="table-container">
                        <table className="data-table" style={{ tableLayout: 'fixed', minWidth: 1500, overflowWrap: 'anywhere' }}>
                            <colgroup>
                                {[3, 12, 10, 20, 14, 16, 6, 4, 8, 4, 3].map((width, index) => (
                                    <col key={index} style={{ width: `${width}%` }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input type="checkbox" className="checkbox"
                                            checked={filtered.length > 0 && selectedIds.size === filtered.length}
                                            onChange={toggleAll} />
                                    </th>
                                    <th>姓名</th>
                                    <th>职位</th>
                                    <th style={{ minWidth: 240 }}>所属领域</th>
                                    <th>公司</th>
                                    <th>邮箱</th>
                                    <th>国家</th>
                                    <th>语言</th>
                                    <th>获客活动</th>
                                    <th>状态</th>
                                    <th>邮件数</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>未找到联系人</td></tr>
                                ) : (
                                    filtered.map(l => (
                                        <tr key={l.id}
                                            className={selectedLead?.id === l.id ? 'selected' : ''}
                                            style={{ cursor: 'pointer' }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" className="checkbox"
                                                    checked={selectedIds.has(l.id)}
                                                    onChange={() => toggleSelect(l.id)} />
                                            </td>
                                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}
                                                onClick={() => selectLead(l)}>{l.name}</td>
                                            <td onClick={() => selectLead(l)}>{l.title}</td>
                                            <td onClick={() => selectLead(l)} style={{ minWidth: 240, maxWidth: 340, lineHeight: 1.65, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                {getPracticeAreas(l) || <span style={{ color: 'var(--text-dim)' }}>未提供</span>}
                                            </td>
                                            <td onClick={() => selectLead(l)}>{l.companyName}</td>
                                            <td><a href={`mailto:${l.email}`} style={{ color: 'var(--accent-light)', textDecoration: 'none' }}
                                                onClick={(e) => e.stopPropagation()}>{l.email}</a></td>
                                            <td onClick={() => selectLead(l)}>{REGION_ICONS[l.country] || '🌍'} {l.country}</td>
                                            <td onClick={() => selectLead(l)}>
                                                {(() => {
                                                    const lang = getCountryLanguage(l.country); return (
                                                        <span style={{
                                                            fontSize: 11, padding: '2px 6px', borderRadius: 4,
                                                            background: isEnglishCountry(l.country) ? 'transparent' : 'rgba(168,85,247,0.15)',
                                                            color: isEnglishCountry(l.country) ? 'var(--text-muted)' : '#c084fc',
                                                        }}>
                                                            {isEnglishCountry(l.country) ? 'EN' : `${lang.nativeName}`}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td onClick={() => selectLead(l)} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                                {campaigns.find(c => c.id === l.campaignId)?.name || '—'}
                                            </td>
                                            <td onClick={() => selectLead(l)}><span className={`badge badge-${zh(l.status)}`}>{zh(l.status)}</span></td>
                                            <td onClick={() => selectLead(l)}>{l.emailsSentCount}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Detail panel */}
                {selectedLead && (
                    <div className="card" style={{ position: 'sticky', top: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedLead.name}</div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{selectedLead.title}</div>
                                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedLead.companyName}</div>
                            </div>
                            <button className="btn btn-outline btn-sm" onClick={() => setSelectedLead(null)}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="section-title">所属领域</div>
                                <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                    {getPracticeAreas(selectedLead) || '未提供'}
                                </div>
                            </div>
                            <div>
                                <div className="section-title">邮箱</div>
                                <a href={`mailto:${selectedLead.email}`} style={{ color: 'var(--accent-light)', fontSize: 13 }}>{selectedLead.email}</a>
                            </div>
                            <div>
                                <div className="section-title">国家</div>
                                <span style={{ fontSize: 13 }}>{REGION_ICONS[selectedLead.country] || '🌍'} {selectedLead.country}</span>
                            </div>
                            <div>
                                <div className="section-title">状态</div>
                                <span className={`badge badge-${zh(selectedLead.status)}`}>{zh(selectedLead.status)}</span>
                            </div>
                            <div>
                                <div className="section-title">语言</div>
                                {(() => {
                                    const lang = getCountryLanguage(selectedLead.country); return (
                                        <span style={{ fontSize: 13 }}>
                                            {lang.flag} {lang.nativeName}
                                            {!isEnglishCountry(selectedLead.country) && (
                                                <span style={{ fontSize: 10, color: '#f59e0b', marginLeft: 4 }}>🌐 自动翻译</span>
                                            )}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div>
                                <div className="section-title">已发送邮件</div>
                                <span style={{ fontSize: 13 }}>{selectedLead.emailsSentCount}</span>
                            </div>
                        </div>

                        {/* Quick send button */}
                        <button className="btn btn-primary btn-block" style={{ marginBottom: 16 }}
                            onClick={() => {
                                setSelectedIds(new Set([selectedLead.id]));
                                setShowCompose(true);
                            }}>
                            ✉️ 发送邮件至 {selectedLead.name}
                        </button>

                        <div className="section-title">邮件往来记录</div>
                        {threads.length === 0 ? (
                            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>暂无邮件</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                                {threads.map(t => (
                                    <div key={t.id} style={{
                                        padding: '10px 12px',
                                        background: t.direction === 'outbound' ? 'var(--accent-bg)' : 'var(--bg-elevated)',
                                        borderRadius: 'var(--radius-md)', fontSize: 12,
                                    }}>
                                        <div style={{ color: 'var(--text-muted)', marginBottom: 4, fontSize: 10 }}>
                                            {t.direction === 'outbound' ? "→ 已发送" : "← 已收到"} · {new Date(t.receivedAt).toLocaleString()}
                                        </div>
                                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.subject}</div>
                                        <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{t.bodyText.slice(0, 200)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ─── Compose & Auto-Send Modal ─── */}
            {showCompose && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !sending) setShowCompose(false); }}>
                    <div className="modal-panel" style={{ width: 720 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h2 style={{ fontSize: 18, fontWeight: 800 }}>
                                    ✉️ 批量发送至 {selectedLeads.length} 联系人{selectedLeads.length > 1 ? 's' : ''}
                                </h2>
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                                    系统将根据每位联系人的资料，自动填入邮件变量。
                                </p>
                                {/* Language badges */}
                                {(() => {
                                    const langs = getLanguagesForCountries(selectedLeads.map(l => l.country));
                                    const hasNonEnglish = langs.some(l => l.code !== 'en');
                                    return hasNonEnglish ? (
                                        <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>🌐 自动翻译：</span>
                                            {langs.filter(l => l.code !== 'en').map(l => (
                                                <span key={l.code} style={{
                                                    fontSize: 10, padding: '2px 8px', borderRadius: 4,
                                                    background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                                }}>
                                                    {l.flag} {l.nativeName}
                                                </span>
                                            ))}
                                            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                                                （用英文撰写 → 按收件人国家自动翻译）
                                            </span>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            {!sending && <button className="btn btn-outline btn-sm" onClick={() => setShowCompose(false)}>✕</button>}
                        </div>

                        {/* Recipients preview */}
                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">收件人（{selectedLeads.length})</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 80, overflowY: 'auto' }}>
                                {selectedLeads.map(l => (
                                    <span key={l.id} style={{
                                        padding: '3px 10px', borderRadius: 'var(--radius-pill)',
                                        background: 'var(--accent-bg)', color: 'var(--accent-light)',
                                        fontSize: 11, fontWeight: 600,
                                    }}>
                                        {l.name} · {l.companyName} · &lt;{l.email}&gt;
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* From — region-locked */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">
                                发件账号
                                {commonRegion && (
                                    <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent-light)', fontWeight: 400 }}>
                                        🔒 发件地区限定为 {getRegionLabel(commonRegion)}
                                    </span>
                                )}
                            </label>
                            <select className="input" value={effectiveFromEmail} onChange={(e) => setFromEmail(e.target.value)}>
                                {allowedSenders.map(s => (
                                    <option key={s.email} value={s.email}>{s.displayName} &lt;{s.email}&gt;</option>
                                ))}
                            </select>
                        </div>

                        {/* Subject */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">邮件主题</label>
                            <input className="input" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="例如：致 {{company}} 的 {{name}} 的合作邀请" />
                        </div>

                        {/* Body */}
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label className="section-title">邮件正文</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {selectedLeads.length > 0 && (
                                        <button className="btn btn-outline btn-sm"
                                            onClick={() => { setShowPreview(!showPreview); setPreviewIndex(0); }}>
                                            {showPreview ? "✏️ 编辑" : `👁️ 预览（${selectedLeads.length}）`}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {showPreview && selectedLeads.length > 0 ? (
                                <div>
                                    {/* Preview navigation */}
                                    <div style={{
                                        padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                        marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    }}>
                                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                            当前预览： <strong>{selectedLeads[previewIndex]?.name}</strong> @ {selectedLeads[previewIndex]?.companyName}
                                            {' '}({previewIndex + 1}/{selectedLeads.length})
                                        </span>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button className="btn btn-outline btn-sm"
                                                disabled={previewIndex === 0}
                                                onClick={() => setPreviewIndex(i => i - 1)}>◀</button>
                                            <button className="btn btn-outline btn-sm"
                                                disabled={previewIndex === selectedLeads.length - 1}
                                                onClick={() => setPreviewIndex(i => i + 1)}>▶</button>
                                        </div>
                                    </div>
                                    {/* Preview subject */}
                                    <div style={{
                                        padding: '8px 12px', background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                                        borderRadius: 'var(--radius-sm)', marginBottom: 4, fontSize: 13, color: 'var(--text-primary)',
                                    }}>
                                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>主题： </span>
                                        {renderTemplate(emailSubject, getPreviewVars(selectedLeads[previewIndex]))}
                                    </div>
                                    {/* Preview body */}
                                    <div style={{
                                        padding: 16, background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                                        borderRadius: 'var(--radius-sm)', fontSize: 13, whiteSpace: 'pre-wrap',
                                        color: 'var(--text-primary)', minHeight: 160,
                                    }}>
                                        {renderTemplate(emailBody, getPreviewVars(selectedLeads[previewIndex]))}
                                    </div>
                                </div>
                            ) : (
                                <textarea className="input" value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                                    rows={8} placeholder="您好 {{name}}，&#10;&#10;我了解到 {{company}}…" />
                            )}
                        </div>

                        {/* Placeholder tags */}
                        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {PLACEHOLDERS.map(p => (
                                <span key={p} style={{
                                    padding: '2px 8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                    fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
                                }}
                                    onClick={() => setEmailBody(prev => prev + p)}
                                    title={`点击插入 ${p}`}
                                >
                                    {p}
                                </span>
                            ))}
                        </div>

                        {/* Sending progress */}
                        {sending && (
                            <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>📤 正在发送…</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-light)' }}>
                                        {sendProgress.sent} / {sendProgress.total}
                                    </span>
                                </div>
                                <div className="progress-bar" style={{ height: 6 }}>
                                    <div className="progress-fill" style={{
                                        width: `${sendProgress.total > 0 ? sendProgress.sent / sendProgress.total * 100 : 0}%`,
                                        background: 'var(--success)',
                                    }} />
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            {!sending && <button className="btn btn-outline" onClick={() => setShowCompose(false)}>取消</button>}
                            <button className="btn btn-success btn-lg" onClick={autoSend}
                                disabled={sending || !fromEmail || !emailSubject || !emailBody}>
                                {sending
                                    ? `正在发送 ${sendProgress.sent}/${sendProgress.total}…`
                                    : `📤 向 ${selectedLeads.length} 位联系人自动发送`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
